import * as React from "react";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem, { MenuItemProps } from "@mui/material/MenuItem";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { SxProps, Theme } from "@mui/material/styles";
import { useAppTheme } from "../../utils/theme";

interface Option {
	value: string;
	onClick: () => void;
	disabled?: boolean;
	sx?: MenuItemProps;
}

interface ActionMenuProps {
	id?: string;
	options: Option[];
	isSquare?: boolean;
	/**
	 * Style overrides merged onto the trigger button, applied after the `isSquare`
	 * defaults so a call site can adjust one property without restyling the shared
	 * component for every screen that uses it.
	 */
	buttonSx?: SxProps<Theme>;
	/**
	 * Optional data-testid for the trigger (SIMS-9274 / portal Q-6). Rows repeat, so a
	 * spec scopes this id inside its row. Additive: omitted, nothing is rendered.
	 */
	testId?: string;
}

const ITEM_HEIGHT = 48;

export default function ActionMenu({
	id,
	options,
	isSquare = false,
	buttonSx,
	testId,
}: ActionMenuProps) {
	const theme = useAppTheme();
	const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
	const open = Boolean(anchorEl);
	const handleClick = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(event.currentTarget);
	};
	const handleClose = () => {
		setAnchorEl(null);
	};

	return (
		<div>
			<IconButton
				aria-label="more"
				id={"actionMenu-button-" + id}
				aria-controls={open ? "actionMenu-menu-" + id : undefined}
				aria-expanded={open ? "true" : undefined}
				aria-haspopup="true"
				data-testid={testId}
				onClick={handleClick}
				{...(isSquare && { size: "large" as const })}
				sx={[
					isSquare && {
						borderRadius: "8px",
						border: `1px solid ${theme.palette.outline.dark}`,
						width: "3rem",
					},
					// Applied last so a call site can override any of the above.
					...(Array.isArray(buttonSx) ? buttonSx : [buttonSx]),
				]}
			>
				<MoreVertIcon sx={{ color: (theme) => theme.palette.icon.dark }} />
			</IconButton>
			<Menu
				id={"actionMenu-menu-" + id}
				MenuListProps={{
					"aria-labelledby": "actionMenu-button-" + id,
				}}
				anchorEl={anchorEl}
				open={open}
				onClose={handleClose}
				slotProps={{
					paper: {
						style: {
							maxHeight: ITEM_HEIGHT * 4.5,
							minWidth: "80px",
						},
					},
				}}
				anchorOrigin={{
					vertical: "top",
					horizontal: "right",
				}}
				transformOrigin={{
					vertical: "top",
					horizontal: "right",
				}}
			>
				{options.map((option, key) => (
					<MenuItem
						key={`${id}-menu-item-${key}`}
						onClick={() => {
							option.onClick();
							handleClose();
						}}
						sx={{
							...option.sx,
							fontSize: "0.875rem",
							justifyContent: "center",
							padding: "0.5rem 0.5rem",
							cursor: !option.disabled ? "pointer" : "none",
						}}
						disabled={option.disabled}
					>
						{option.value}
					</MenuItem>
				))}
			</Menu>
		</div>
	);
}
